import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateEvent } from '@/hooks/useEvents';
import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2 } from 'lucide-react';
import type { Database } from '@/integrations/supabase/types';

type EventLocationType = Database['public']['Enums']['event_location_type'];

export function CreateEventDialog() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [locationType, setLocationType] = useState<EventLocationType>('in_person');
  const [locationAddress, setLocationAddress] = useState('');
  const [virtualLink, setVirtualLink] = useState('');
  const [cohortTag, setCohortTag] = useState('');
  const [maxAttendees, setMaxAttendees] = useState('');

  const createEvent = useCreateEvent();
  const { data: profile } = useProfile();
  const { toast } = useToast();

  const defaultCohort = profile?.city_cohort || 'global';

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setEventDate('');
    setLocationType('in_person');
    setLocationAddress('');
    setVirtualLink('');
    setCohortTag('');
    setMaxAttendees('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !eventDate) return;

    try {
      await createEvent.mutateAsync({
        title,
        description: description || null,
        event_date: new Date(eventDate).toISOString(),
        location_type: locationType,
        location_address: locationAddress || null,
        virtual_link: virtualLink || null,
        cohort_tag: cohortTag || defaultCohort,
        max_attendees: maxAttendees ? parseInt(maxAttendees, 10) : null,
      });
      toast({ title: 'Event created', description: 'Your event is now visible to members.' });
      resetForm();
      setOpen(false);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const showAddress = locationType === 'in_person' || locationType === 'hybrid';
  const showVirtualLink = locationType === 'virtual' || locationType === 'hybrid';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-qbn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Create Event
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Event</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Event name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this event about?"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="eventDate">Date & Time *</Label>
            <Input
              id="eventDate"
              type="datetime-local"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Location Type</Label>
            <Select
              value={locationType}
              onValueChange={(v) => setLocationType(v as EventLocationType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="in_person">In Person</SelectItem>
                <SelectItem value="virtual">Virtual</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {showAddress && (
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={locationAddress}
                onChange={(e) => setLocationAddress(e.target.value)}
                placeholder="Event location"
              />
            </div>
          )}

          {showVirtualLink && (
            <div className="space-y-2">
              <Label htmlFor="virtualLink">Virtual Link</Label>
              <Input
                id="virtualLink"
                value={virtualLink}
                onChange={(e) => setVirtualLink(e.target.value)}
                placeholder="https://zoom.us/..."
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="cohort">Cohort</Label>
            <Input
              id="cohort"
              value={cohortTag}
              onChange={(e) => setCohortTag(e.target.value)}
              placeholder={defaultCohort}
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to default to your city ({defaultCohort}). Use "global" for all members.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxAttendees">Max Attendees</Label>
            <Input
              id="maxAttendees"
              type="number"
              value={maxAttendees}
              onChange={(e) => setMaxAttendees(e.target.value)}
              placeholder="No limit"
              min="1"
            />
          </div>

          <Button
            type="submit"
            disabled={createEvent.isPending || !title || !eventDate}
            className="w-full btn-qbn-primary"
          >
            {createEvent.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Create Event
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
